import React from "react";

import { cn } from "@/lib/utils";

/**
 * A toggle pill that narrows a list: the markup, states and height contract the "Questions" chip has
 * always had, with the label as a prop so a second filter wears the same affordance instead of a
 * bespoke one (SV-142's "Sensitive" chip on the search page is the second consumer).
 *
 * <p>It owns no state and writes no history (rule 33): the host keeps the flag in its URL-backed route
 * state and hands it down. Pass an already-localized label; this component renders no copy of its own.
 *
 * <p><b>Its height deliberately matches `SimpleSelect size="sm"` exactly</b> — `min-h-9`, rising to
 * `min-h-11` under a coarse pointer for the 44px tap target. See `QuestionsFilterChip` for why a chip
 * even 2px shorter than the sibling select shifts a measured layout invariant on the group page.
 */
export default function FilterToggleChip({
    active,
    onToggle,
    label,
    disabled = false,
    dataPlaywrightId,
}: {
    active: boolean;
    /** Called with the NEXT value, never with a toggle signal — the host decides what to do with it. */
    onToggle: (next: boolean) => void;
    /** Already localized. */
    label: string;
    disabled?: boolean;
    dataPlaywrightId: string;
}) {
    return (
        <button
            type="button"
            disabled={disabled}
            aria-pressed={active}
            onClick={() => onToggle(!active)}
            data-playwright-id={dataPlaywrightId}
            className={cn(
                "inline-flex min-h-9 shrink-0 items-center rounded-full border px-3 text-sm transition-colors",
                // The one shared focus ring (rule 23), not a bespoke 2px + offset variant — this chip
                // sits directly beside a SimpleSelect, and two adjacent controls focusing differently
                // is the thing the shared token exists to prevent.
                "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
                "disabled:cursor-not-allowed disabled:opacity-60",
                "[@media(pointer:coarse)]:min-h-11",
                active
                    ? "border-primary bg-primary/10 text-foreground"
                    // Hover is the wash alone. The text-colour flip design rule 3 forbids was also
                    // redundant here — `hover:bg-accent` is already a sufficient affordance.
                    // DISCOVERY-30: a resting surface, not bare page. Inactive, this used to paint
                    // muted text on the background with nothing but a hairline to say it was a
                    // control, directly beside an inert query echo that was painted in full
                    // foreground on a fill. It is a toggle, so it looks like one at rest.
                    : "border-border bg-background2/60 text-foreground hover:bg-accent",
            )}
        >
            {label}
        </button>
    );
}
